/* ── 213B §15 — A FREE WINDOW'S DATE INPUT ──
 *
 * The rule: a free window never shows a date it does not narrow by, and it
 * narrows only by a complete date. Reproduced before writing (the drive's
 * --part=free and its minimal twin, scratch date-harness, both engines):
 *
 *   · Chromium, the year typed digit by digit: a date input fires `input` at
 *     the FIRST year digit — the component held « 0002-03-31 », then 0020-,
 *     0202-, and the list narrowed by a year-2 date three keystrokes long (the
 *     « to » edge emptied the table, each keystroke through `narrow()`).
 *   · Chromium, a click in the middle of the field lands on the MONTH segment
 *     (the field is 99 px): a full date typed from there leaves the field
 *     showing digits with `validity.badInput` and a value of "" — a date shown
 *     that nothing narrows by. Browser-native (the vanilla twins do the same).
 *   · WebKit here (Linux, headless) takes no keystroke in a date input at all;
 *     Safari's segment editor is macOS's and out of the tree's reach.
 *
 * So the DOM owns the typing (uncontrolled: React never writes the value back
 * mid-typing), the component is told only a complete date — a four-digit year
 * not starting with 0 (a year being typed, or typed too far) — or an explicit
 * clear, and on blur the
 * display is made to show what is held — by a remount when the browser leaves
 * typed segments on an empty value (Safari does). */
import React, { forwardRef, useEffect, useRef, useState } from "react";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "defaultValue"> & {
  /** What the component holds: an ISO day, or "" for no edge. */
  value: string;
  /** Told a complete date (ISO) the list may narrow by, or "" when the edge was cleared. */
  onCommit: React.Dispatch<string>;
};

/** Whether `el` shows exactly `held`: the same value, and no typed segments left on an empty one. */
const showsHeld = (el: HTMLInputElement, held: string) => el.value === held && !(held === "" && el.validity.badInput);

const FreeDateInput = forwardRef<HTMLInputElement, Props>(function FreeDateInput({ value, onCommit, onBlur, ...rest }, ref) {
  const inner = useRef<HTMLInputElement | null>(null);
  // a remount is the one way to make a browser drop segments it keeps showing over an empty value
  const [epoch, setEpoch] = useState(0);
  const setRef = (el: HTMLInputElement | null) => {
    inner.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  /** The display made to show what is held (an edge cleared from outside, another period then « free » again). */
  const resync = (el: HTMLInputElement) => {
    if (showsHeld(el, value)) return;
    el.value = value;
    if (!showsHeld(el, value)) setEpoch((n) => n + 1);
  };
  useEffect(() => {
    const el = inner.current;
    // `globalThis`: eslint.config.mjs declares no browser globals for src/**
    if (el && globalThis.document.activeElement !== el) resync(el);
  });
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === "") {
      if (e.target.validity.badInput) return; // segments being typed or cleared one by one: nothing to hold yet
      if (value !== "") onCommit("");
      return;
    }
    // a complete date has a four-digit year not starting with 0: « 0002-03-31 » is a year being typed, « 22026-03-10 »
    // one typed too far (Chromium's year segment takes six digits) — neither is a date the list should narrow by
    if (!/^[1-9]\d{3}-\d{2}-\d{2}$/.test(v)) return;
    if (v !== value) onCommit(v);
  };
  const onBlurInner = (e: React.FocusEvent<HTMLInputElement>) => {
    resync(e.currentTarget);
    if (onBlur) onBlur(e);
  };
  return <input key={epoch} ref={setRef} type="date" defaultValue={value} onChange={onChange} onBlur={onBlurInner} {...rest} />;
});

export default FreeDateInput;
